/**
 * Significant Change Notification (SCN) question set — V96-3-6 / ADR-0045.
 *
 * Derived from FedRAMP Consolidated Rules 2026 (FRR SCN, release
 * 2026.09.13.02). Every question cites the SCN rule whose statement,
 * key tests or examples it restates, so a reviewer can trace each
 * classification back to FedRAMP text:
 *
 *   SCN-CSO-EVA  evaluation order: class change → routine → transformative → adaptive
 *   SCN-RTR-NNR  routine recurring: regular, no breaking change, no exec approval
 *   SCN-TRF-TPR  transformative key tests + examples
 *   SCN-ADP-NTF  adaptive key tests + examples (and the default bucket)
 *
 * Notice rules (which anchor, before/after) are also data here; the
 * *numbers* are never in code — they come from `certification_timeframes`
 * (populated from the same JSON via the reviewed ingest).
 */
export const SCN_QUESTION_SET_VERSION = 'scn-cr26-2026.09.13.02';
export const SCN_QUESTIONS = [
    // --- SCN-CSO-EVA: class change is out of SCN scope -------------------------
    {
        id: 'cert_class_change',
        text: 'Does the change alter the FedRAMP Certification class of the entire offering?',
        effect: 'certification_class_change',
        rule_ids: ['SCN-CSO-EVA'],
        basis: 'Is it a FedRAMP Certification class change? --> This requires a new assessment and cannot be done under the Significant Change Notification rules.',
    },
    // --- SCN-TRF-TPR: transformative ------------------------------------------
    {
        id: 'trf_critical_third_party',
        text: 'Does it add, remove or replace a critical third-party service that handles a significant portion of information (e.g. an IaaS change)?',
        effect: 'transformative',
        rule_ids: ['SCN-TRF-TPR'],
        basis: 'The addition, removal, or replacement of a critical third party service that handles a significant portion of information (e.g., IaaS change)',
    },
    {
        id: 'trf_categorization_increase',
        text: 'Does it increase the security categorization of a service in the offering that handles federal customer data?',
        effect: 'transformative',
        rule_ids: ['SCN-TRF-TPR'],
        basis: 'Increasing the security categorization of a service within the offering that actively handles federal customer data',
    },
    {
        id: 'trf_management_plane',
        text: 'Does it replace the underlying management plane or shift the workload orchestration paradigm (e.g. VMs to containers)?',
        effect: 'transformative',
        rule_ids: ['SCN-TRF-TPR'],
        basis: 'Replacement of underlying management planes or paradigm shift in workload orchestration',
    },
    {
        id: 'trf_boundary_data_move',
        text: 'Does it move large amounts of federal customer data across boundaries in a way that differs from normal operations (e.g. datacenter migration)?',
        effect: 'transformative',
        rule_ids: ['SCN-TRF-TPR'],
        basis: 'Datacenter migration where large amounts of federal customer data is moved across boundaries different from normal day-to-day operations',
    },
    {
        id: 'trf_new_ai_capability',
        text: 'Does it add an AI capability that uses federal customer data differently from existing services (new third-party AI service, training on customer data)?',
        effect: 'transformative',
        rule_ids: ['SCN-TRF-TPR'],
        basis: 'Adding a new AI-based capability that impacts federal customer data in a different way than existing services or capabilities',
    },
    {
        id: 'trf_risk_profile',
        text: 'Does it alter the service risk profile or require new or significantly different customer responsibilities?',
        effect: 'transformative',
        rule_ids: ['SCN-TRF-TPR'],
        basis: 'Alters the service risk profile or require new or significantly different actions to address customer responsibilities',
    },
    {
        id: 'trf_extensive_reassessment',
        text: 'Does it require extensive updates to security assessments, documentation, and how many security requirements are met and validated?',
        effect: 'transformative',
        rule_ids: ['SCN-TRF-TPR'],
        basis: 'Requires extensive updates to security assessments, documentation, and how a large number of security requirements are met and validated',
    },
    // --- SCN-ADP-NTF: adaptive -------------------------------------------------
    {
        id: 'adp_breaking_update',
        text: 'Is it an update to operating systems, containers, VMs, software or libraries with known breaking changes, complex steps or service disruption?',
        effect: 'adaptive',
        rule_ids: ['SCN-ADP-NTF'],
        basis: 'Updates to operating systems, containers, virtual machines, software or libraries with known breaking changes, complex steps, or service disruption',
    },
    {
        id: 'adp_large_feature',
        text: 'Is it a larger-than-normal feature change (weeks of work) that is not a major new service?',
        effect: 'adaptive',
        rule_ids: ['SCN-ADP-NTF'],
        basis: 'Deploying larger than normal incremental feature improvements ... but are not considered a major new service',
    },
    {
        id: 'adp_crypto_module_equivalent',
        text: 'Does it change a cryptographic module to one meeting the same standards and characteristics?',
        effect: 'adaptive',
        rule_ids: ['SCN-ADP-NTF'],
        basis: 'Changing cryptographic modules where the new module meets the same standards and characteristics of the former',
    },
    {
        id: 'adp_like_for_like_component',
        text: 'Does it replace a component like-for-like where some security plan or procedure adjustments are needed (e.g. scanner or managed database swap)?',
        effect: 'adaptive',
        rule_ids: ['SCN-ADP-NTF'],
        basis: 'Replacing a like-for-like component where some security plan or procedure adjustments are required',
    },
    {
        id: 'adp_ai_model_existing_service',
        text: 'Does it add models to an already-approved AI service without exposing federal customer data to new services?',
        effect: 'adaptive',
        rule_ids: ['SCN-ADP-NTF'],
        basis: 'Adding models to existing approved AI services without exposing federal customer data to new services',
    },
    {
        id: 'adp_security_docs_update',
        text: 'Does it need changes to security plans or procedures, or verification of secure configuration after implementation?',
        effect: 'adaptive',
        rule_ids: ['SCN-ADP-NTF'],
        basis: 'Requires minimal changes to security plans or procedures; requires verification of existing functionality and secure configuration after implementation',
    },
    // --- SCN-RTR-NNR: routine recurring -----------------------------------------
    {
        id: 'rtr_regular',
        text: 'Is this an activity you perform regularly and routinely (patching, signatures, capacity, firewall rules, token refresh)?',
        effect: 'routine_gate',
        rule_ids: ['SCN-RTR-NNR'],
        basis: 'If the activity does not occur regularly and routinely then it cannot be a significant change of this type',
    },
    {
        id: 'rtr_breaking_or_disruptive',
        text: 'Does it involve breaking changes, refactoring or migration, or a major impact on service availability?',
        effect: 'routine_disqualifier',
        rule_ids: ['SCN-RTR-NNR'],
        basis: 'No major impact to service availability; significant refactoring or migration process NOT required; no breaking changes',
    },
    {
        id: 'rtr_exec_approval',
        text: 'Does it require executive approval?',
        effect: 'routine_disqualifier',
        rule_ids: ['SCN-RTR-NNR'],
        basis: 'Does not require executive approval',
    },
];
/**
 * Notice obligations per classification. Routine changes carry none
 * (SCN-RTR-NNR: "SHOULD NOT make formal Significant Change Notifications").
 */
export const SCN_NOTICE_RULES = [
    { rule_id: 'SCN-ADP-NTF', classification: 'adaptive', anchor: 'completed', direction: 'after', statement_basis: 'business days after finishing adaptive changes' },
    { rule_id: 'SCN-TRF-NIP', classification: 'transformative', anchor: 'planned_start', direction: 'before', statement_basis: 'business days before starting transformative changes' },
    { rule_id: 'SCN-TRF-NFP', classification: 'transformative', anchor: 'planned_start', direction: 'before', statement_basis: 'business days before starting transformative changes' },
    { rule_id: 'SCN-TRF-NAF', classification: 'transformative', anchor: 'completed', direction: 'after', statement_basis: 'business days after finishing transformative changes' },
    { rule_id: 'SCN-TRF-NAV', classification: 'transformative', anchor: 'verified', direction: 'after', statement_basis: 'business days after completing the verification, assessment, and/or validation of transformative changes' },
    { rule_id: 'SCN-TRF-UPD', classification: 'transformative', anchor: 'completed', direction: 'after', statement_basis: 'business days after finishing transformative changes' },
];
//# sourceMappingURL=scn-questions.js.map